'use client'

import type { MouseEvent, ReactNode } from 'react'

import { browserNavigate } from '@/lib/browser-navigation'
import { useDisplayCurrency } from '@/lib/currency/display-currency'
import { addressWithDisplayCurrency } from '@/lib/currency/link'

interface CrawlSafeLinkProps {
    /**
     * The fully-resolved destination, locale prefix and all. Resolved by the caller
     * because navigation here is a full page load, often to a different app —
     * nothing downstream gets a chance to add the prefix an i18n `Link` would.
     */
    url: string
    /** Opens the destination in a new tab, as `target="_blank"` would. */
    newTab?: boolean
    /** Fired before navigation, so an analytics SDK has a chance to flush. */
    onSelect?: () => void
    /**
     * The address already states the currency it means — including by naming none
     * deliberately. Only the currency control sets this: it is the one surface that
     * *authors* a currency rather than inheriting the page's, so stamping the page's
     * selection on would overwrite the choice being made and leave the locale's own
     * default unreachable. The same exception an address that already names a currency
     * gets, extended to one whose absence of a value is just as deliberate.
     */
    currencyIsAuthored?: boolean
    className?: string
    /** Rendered as `data-testid`, for a caller whose own tests already select on one. */
    testId?: string
    children: ReactNode
}

/**
 * A link-like control for a destination that **must not appear in the rendered HTML** —
 * it carries the URL in JS rather than an `href`, so a crawler never sees it.
 * `rel="nofollow"` is not sufficient: that is a ranking hint, not a fetch ban. The
 * transient search routes are the archetype (`invariants.md#no-transient-search-links`),
 * but nothing here is search-specific — any never-index destination belongs on it.
 *
 * A `<button>`, not an hrefless `<a>`: that has a generic role, so it would need
 * `tabIndex` + `role` + key handlers to reimplement `<button>`, and would recover none of
 * the link affordances, which come from the `href` rather than the tag. What is given up
 * is the context menu and the status-bar URL preview — the paths by which the URL would
 * escape. Full rationale: `plans/repo/2026-08-10-no-transient-search-links.md`.
 */
export function CrawlSafeLink({
    url,
    newTab,
    onSelect,
    currencyIsAuthored,
    className,
    testId,
    children,
}: CrawlSafeLinkProps) {
    // The currency is applied here rather than by each caller for the same reason the
    // locale prefix cannot be: this control is the last thing to hold the address before
    // the browser does. A caller that resolves a destination early has no seam left after
    // it, and there is one rule to keep right instead of one per destination.
    const selectedCurrency = useDisplayCurrency()
    const destination = currencyIsAuthored ? url : addressWithDisplayCurrency(url, selectedCurrency)

    function handleClick() {
        // Fire analytics before navigation so the SDK has a chance to flush.
        onSelect?.()
        browserNavigate(destination, { newTab })
    }

    // The middle button fires `auxclick`, not `click`, so without this it does nothing at
    // all. Right-click fires it too, hence the button check.
    function handleAuxClick(event: MouseEvent<HTMLButtonElement>) {
        if (event.button !== 1) {
            return
        }
        event.preventDefault()
        onSelect?.()
        browserNavigate(destination, { newTab: true })
    }

    // `data-crawl-safe-link` serves one assertion: enumerating every control of this class
    // on a page. Role selection can name an individual control but not the class.
    return (
        <button
            type="button"
            data-crawl-safe-link=""
            data-testid={testId}
            onClick={handleClick}
            onAuxClick={handleAuxClick}
            className={className}
        >
            {children}
        </button>
    )
}
